import { Box, Link, type SxProps, type Theme } from '@mui/material';
import { type FC, type MouseEvent, type ReactNode, useLayoutEffect, useRef, useState } from 'react';

import { hasValue } from '@core/utils/equality';

const DEFAULT_LINE_CLAMP = 2;

type Props = {
  children: ReactNode;
  lines?: number;
  moreLabel?: string;
  lessLabel?: string;
  sx?: SxProps<Theme>;
};

export const ClampedText: FC<Props> = ({
  children,
  lines = DEFAULT_LINE_CLAMP,
  moreLabel = 'Show More',
  lessLabel = 'Show Less',
  sx,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const element = textRef.current;
    if (!hasValue(element) || isExpanded) return undefined;

    const measure = (): void => setIsOverflowing(element.scrollHeight > element.clientHeight + 1);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return (): void => observer.disconnect();
  }, [children, isExpanded, lines]);

  const toggle = (event: MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    setIsExpanded((expanded) => !expanded);
  };

  return (
    <Box component='span' sx={{ display: 'block', ...sx }}>
      <Box
        component='span'
        ref={textRef}
        sx={{
          wordBreak: 'break-word',
          ...(isExpanded
            ? { display: 'block' }
            : {
                display: '-webkit-box',
                WebkitLineClamp: lines,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }),
        }}
      >
        {children}
      </Box>
      {isOverflowing && (
        <Link
          component='button'
          type='button'
          variant='inherit'
          underline='hover'
          onClick={toggle}
          sx={{ verticalAlign: 'baseline' }}
        >
          {isExpanded ? lessLabel : moreLabel}
        </Link>
      )}
    </Box>
  );
};

export default ClampedText;
